import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, UtensilsCrossed, ShoppingBag, Truck } from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import MenuItemCard from "@/components/shared/MenuItemCard";
import ItemDetailModal from "@/components/shared/ItemDetailModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicMenu, MenuItem } from "@/hooks/usePublicMenu";
import { useSessionContext } from "@/contexts/SessionContext";
import { Button } from "@/components/ui/button";

export default function ModernHome() {
  const { t } = useLanguage();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const { session } = useSessionContext();
  const { menuItems, loading } = usePublicMenu(session.order.branchId);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  const popularItems = menuItems.filter((item) => item.popular);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Modern Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-secondary" />
          <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-background/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
                <Zap className="w-4 h-4 text-primary-foreground" />
                <span className="text-sm font-medium text-primary-foreground">
                  طلب في خلال لحظة
                </span>
              </div>
              <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black text-primary-foreground leading-[1.1]">
                {t("hero.title")}{" "}
                <span className="block text-primary-foreground/60">
                  {brand.logoType === "emoji" ? brand.logo : brand.name}
                </span>
              </h1>
              <p className="mt-6 text-lg text-primary-foreground/80 max-w-lg">
                {t("hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  className="rounded-full px-6 py-5 gap-2 bg-background text-foreground hover:bg-background/90 font-bold"
                  onClick={() => navigate("/menu")}
                >
                  {t("hero.cta")}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-32 -end-32 w-96 h-96 rounded-full bg-background/10" />
          <div className="absolute -top-16 end-1/4 w-48 h-48 rounded-full bg-background/5" />
        </section>

        {/* Trending Now */}
        <section className="container mx-auto px-4 py-14 md:py-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground">
                🔥 Trending Now
              </h2>
              <p className="text-muted-foreground mt-1">Most loved by our customers</p>
            </div>
            <Button variant="ghost" className="gap-1 font-bold" onClick={() => navigate("/menu")}>
              See all <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl border bg-card animate-pulse aspect-square" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
               {popularItems.slice(0, 8).map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  onClick={setSelectedItem}
                />
              ))}
            </div>
          )}
        </section>

        {/* Stats band */}
        <section className="bg-card border-y">
          <div className="container mx-auto px-4 py-12">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <p className="font-heading text-3xl md:text-4xl font-black text-primary">500+</p>
                <p className="text-sm text-muted-foreground mt-1">Happy Customers</p>
              </div>
              <div>
                <p className="font-heading text-3xl md:text-4xl font-black text-primary">30min</p>
                <p className="text-sm text-muted-foreground mt-1">Avg Delivery</p>
              </div>
              <div>
                <p className="font-heading text-3xl md:text-4xl font-black text-primary">4.9⭐</p>
                <p className="text-sm text-muted-foreground mt-1">Rating</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {selectedItem && (
        <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}
